import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import FileUploadCard from "../../FileUploadCard";
import CIPPaginatedTable from "../../CIPPaginatedTable";
import SkipDialog from "../SkipDialog";
import { uploadBulkCIPs } from "../../../network/cipAPI";
import FileDetailsCard from "../../FileUploadCard/FileDetailsCard";
import {
  CIP_SOURCE_OPTIONS,
  CustomModalButtons,
  CIP_CARD_IDS,
} from "../../../constants/global.constants";
import { deleteCipsBySource } from "../../../network/cipAPI";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../Notifications";

import "./BulkCIP.scss";

const BulkCIP = ({
  setCIPMode,
  CIPNeverShowAgain,
  setCIPNeverShowAgain,
  selectedTrain,
  existingCIPFileName,
  switchTo,
  setSwitchTo,
  disabled,
}) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [tempExistingCIPFileName, setTempExistingCIPFileName] = useState(null); // to remove filename temp when user click remove button
  const [showTable, setShowTable] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [isProceed, setIsProceed] = useState(false);
  const [showTableLoader, setShowTableLoader] = useState(false);

  const { t } = useTranslation();

  const handleDialogClose = ({ target }) => {
    setShowSkipDialog(false);

    if (target.id === CustomModalButtons.CONFIRM) {
      setIsProceed(true);
    }
  };

  const handleInterruptLogic = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSkipDialog(true);
  }, []);

  // fetchCIPs logic moved to PaginatedTable

  const uploadBulkCIPsHandler = async () => {
    if (!uploadedFile) return;
    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      const response = await uploadBulkCIPs(selectedTrain, formData);
      if (response && response.status === 200) {
        setShowTable(true); // Show table after upload
        return { success: true };
      } else {
        const message = response.detail?.error || "File upload failed!";
        return { error: message };
      }
    } catch (err) {
      // give a generic message for unexpected errors
      return { error: "File upload failed!" };
    }
  };

  const resetCIPMode = () => {
    setCIPMode(null);
    setShowTable(false);
    setTempExistingCIPFileName(null);
    setUploadedFile(null);
  };

  const handleRemoveBulkCIPFile = async () => {
    setShowTableLoader(true);
    const source = CIP_SOURCE_OPTIONS.BULK;
    try {
      const res = await deleteCipsBySource(selectedTrain, source);
      if (res && res.status === 200) {
        resetCIPMode();
        createNotification(
          SUCCESS_NOTIFICATION,
          t("notificationStrings.deleteSuccess"),
          ""
        );
      } else {
        throw new Error("Failed to delete CIPs.");
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("ValueStrings.somethingWentWrong"),
        ""
      );
    } finally {
      setShowTableLoader(false);
    }
  };

  useEffect(() => {
    // If a file is uploaded, set mode
    if (uploadedFile) {
      setCIPMode(CIP_SOURCE_OPTIONS.BULK);
      setShowTable(false);
    }
  }, [uploadedFile]);

  useEffect(() => {
    // If an existing file name is present, set temp state
    if (existingCIPFileName) {
      setUploadedFile(null);
      setTempExistingCIPFileName(existingCIPFileName);
      setShowTable(true);
    } else {
      resetCIPMode();
    }
  }, [existingCIPFileName, selectedTrain]);

  // handle switch between modes
  useEffect(() => {
    if (switchTo === CIP_SOURCE_OPTIONS.BULK) {
      const bulkCard = document.getElementById(CIP_CARD_IDS.BULK);
      bulkCard.click();
      setSwitchTo(null);
    } else if (switchTo === CIP_SOURCE_OPTIONS.RECURRENT) {
      setShowTable(false);
      setUploadedFile(null);
      setTempExistingCIPFileName(null);
    }
  }, [switchTo]);

  return (
    <>
      {uploadedFile !== null || tempExistingCIPFileName !== null ? (
        <FileDetailsCard
          uploadedFile={uploadedFile}
          setUploadedFile={setUploadedFile}
          onValidHandler={uploadBulkCIPsHandler}
          tempExistingCIPFileName={tempExistingCIPFileName}
          isUploadComplete={showTable}
          showRemoveIcon
          handleRemoveFile={handleRemoveBulkCIPFile}
          showDeleteConfirmation
        />
      ) : (
        <FileUploadCard
          id={CIP_CARD_IDS.BULK}
          hoverable
          disabled={disabled}
          uploadedFile={uploadedFile}
          setUploadedFile={setUploadedFile}
          isProceed={isProceed}
          setIsProceed={setIsProceed}
          onInterrupt={handleInterruptLogic} // Pass the interrupt logic
          shouldInterrupt={!CIPNeverShowAgain} // Pass the flag to determine if interrupt is needed
        />
      )}

      {showTable && (
        <CIPPaginatedTable
          selectedTrain={selectedTrain}
          isScrollIntoView={uploadedFile}
          source={CIP_SOURCE_OPTIONS.BULK}
          resetCIPMode={resetCIPMode}
          enableActions
          showTableLoader={showTableLoader}
        />
      )}
      {showSkipDialog && (
        <SkipDialog
          onClose={handleDialogClose}
          CIPNeverShowAgain={CIPNeverShowAgain}
          setCIPNeverShowAgain={setCIPNeverShowAgain}
        />
      )}
    </>
  );
};

export default BulkCIP;
